import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { get_deployment, usePost } from "./API";
import { removeDuplicateEvents, safeParse, useAsyncInterval, useInterval } from "./Util";
import { useMultipartUpload } from "./utils/useMultipartUpload";
import { ChangeSet, State, UserActivity } from "@aidkitorg/typesheets/lib/collab";
import { ConfigurationContext, UserInfoContext } from "./Context";
import { useChannel } from "./Realtime";
import { useToast } from "@aidkitorg/component-library";
import { compress, decompress } from "./utils/compress";
import { v4 as uuidv4 } from 'uuid';
import { useAsyncEffect } from "./Hooks/AsyncEffect";
import { BasePubSubEvent } from "@aidkitorg/types/lib/pubsub";

export type DistroPartialChangeEvent = BasePubSubEvent<
  'partial_change',
  {
    compressedChunk: string,
    totalChunks: number,
    userActivity?: UserActivity,
    uuid: string
  }
>;

export type DistroChangeEvent = BasePubSubEvent<
  'change',
  {
    events: ChangeSet,
    userActivity?: UserActivity,
    uuid: string
  }
>;

export type DistroEnableCollabEvent = BasePubSubEvent<
  'enable_collab',
  {
    tabId: string,
    uid?: string,
    tags?: string[],
    refreshContext?: (() => void),
  }
>;

export type DistroDisableCollabEvent = BasePubSubEvent<
  'disable_collab',
  {
    tabId: string,
    uid?: string,
    tags?: string[],
    refreshContext?: (() => void),
  }
>;

export type DistroSavedSurveyEvent = BasePubSubEvent<
  'saved_survey',
  {
    username?: string,
    tabId: string,
  }
>;

export type DistroRemoveUserEvent = BasePubSubEvent<
  'remove_user',
  {
    name: string,
    browserTab: string,
  }
>;

export type ConfigRealtimeEvents =
    DistroPartialChangeEvent
    | DistroChangeEvent
    | DistroEnableCollabEvent
    | DistroDisableCollabEvent
    | DistroSavedSurveyEvent
    | DistroRemoveUserEvent;

interface DistroBranch {
  id: number;
  name: string;
  created_at: string;
  created_by: string | null;
  is_dev_branch: boolean;
}

export function useCollabActions({
  surveyName,
  tabId,
  mergeReceivedEventsCallback,
  autosave = true,
  onSocketDisconnected,
  onSocketConnected,
}: {
  surveyName: string,
  tabId: string,
  mergeReceivedEventsCallback: (events: ChangeSet, userActivity?: UserActivity[], initialize?: boolean, debug?: boolean, unpublishedEvents?: ChangeSet) => void,
  autosave?: boolean,
  onSocketDisconnected?: () => void,
  onSocketConnected?: () => void,
}) {
  const user = useContext(UserInfoContext);
  const config = useContext(ConfigurationContext);
  const [channel, setChannel] = useState('');

  // triggers the backend to autosave a collab version - ie, merge the dynamo events with 
  // the current s3 collab version, prune them to smallest necessary, and save that as the new version.
  const autoSaveCollabVersion = usePost('/survey/trigger_collab_autosave');
  // get collab events and settings
  const getCollabEvents = usePost('/survey/get_collab_events');
  const getCollabEnabled = usePost('/survey/get_collab_enabled');
  // disable collab
  const clearCollabEvents = usePost('/survey/clear_collab_events');
  // save events from the client as a new collab version. Used for migrating onto collab mode, as well as for 
  // very large collections of changes (like when someone copies and pastes something into the distro).
  const writeCollabVersion = usePost('/survey/write_collab_version', { compressRequestPayload: true });
  const getBranches = usePost('/survey/get_branches');

  const multipartUpload = useMultipartUpload();

  // Save LOCAL Collab events to Dynamo
  const saveCollabEvents = usePost('/survey/save_collab_events', { compressRequestPayload: true, keepAlive: true });
  // Your fresh Collab changes that still need to be persisted to Dynamo
  const eventsToSave = useRef<ChangeSet>();
  const saveEventsTimer = useRef<number | undefined>(undefined);
  // Retry timer for failed saves (cancelled if user makes a new change)
  const saveRetryTimer = useRef<number | undefined>(undefined);
  // Track consecutive save failures - alert user after 3 failures
  const consecutiveSaveFailures = useRef<number>(0);

  // Your fresh Collab changes that still need to be shared over Websockets
  const eventsToSend = useRef<ChangeSet>();
  const sendEventsTimer = useRef<number | undefined>(undefined);
  // Retry timer for failed WebSocket sends (cancelled if user makes a new change)
  const sendRetryTimer = useRef<number | undefined>(undefined);

  // Chunks of very large Collab change that are in the process of being received.
  // After final chunk is received, we merge events/state in Distro and this ref is cleared.
  // See: partial_change below
  const eventsAwaitingMerge = useRef<Record<string, ChangeSet[]>>({});

  // counts 3 seconds after the last incoming edit, to re-enable publishing.
  const incomingEditsTimer = useRef<number | undefined>(undefined);

  const [collabEnabled, setCollabEnabled] = useState(false);
  // Signals that the collab check has completed (regardless of whether collab is enabled).
  // Callers should wait for this before initializing the editor to avoid a double-init "blip".
  const [collabReady, setCollabReady] = useState(false);
  const [canPublish, setCanPublish] = useState(true);
  const [branchId, setBranchId] = useState<number | undefined>();
  const [branches, setBranches] = useState<DistroBranch[] | undefined>(undefined);

  const [debug, setDebug] = useState(false);

  const { toast } = useToast();

  useAsyncEffect(async () => {
    const [isCollabEnabled, branches] = await Promise.all([
      getCollabEnabled({ name: surveyName }),
      getBranches({ survey: surveyName }),
    ]);

    setCollabEnabled(isCollabEnabled);

    if (!isCollabEnabled) {
      setCollabReady(true);
      return;
    }

    // For now, assume we're always on the dev branch
    const devBranch = branches.find(({ is_dev_branch }) => is_dev_branch);

    if (!devBranch) {
      throw new Error("Could not find branch");
    }

    setBranchId(devBranch.id);
    setBranches(branches);
    setChannel(get_deployment() + ":" + surveyName + ":" + devBranch.id);
    setCollabReady(true);
  }, [surveyName]);

  const getCollabState = useCallback(async () => {
    if (!collabEnabled) {
      return {
        status: 'ok',
        collabEnabled: false,
        userActivity: {},
        events: [],
        savedVersionURL: '',
        debug: false,
        unpublishedEvents: [],
      };
    }

    if (!branchId) {
      throw new Error("Could not fetch collab state due to missing branch id");
    }

    const collabResp = await getCollabEvents({ name: surveyName, branchId });
    if (collabResp && collabResp.collabEnabled) {
      setCollabEnabled(true);
      setDebug(collabResp.debug);

      let versionEvents: ChangeSet = [];
      if (collabResp.savedVersionURL.length > 0) {
        const response = await fetch(collabResp.savedVersionURL);
        versionEvents = safeParse(await response.text(), []) as ChangeSet;
      }

      return {
        ...collabResp,
        events: [...(Array.isArray(versionEvents) ? versionEvents : []), ...collabResp.events]
      };
    } else {
      // This seems like it is quietly turning off collab on the FE, even when it may still be on on the BE. 
      // setCollabEnabled(false);
      return collabResp;
    }
  }, [surveyName, branchId, getCollabEvents]);

  const eventCallback = useCallback(async (realtimeEvent: ConfigRealtimeEvents) => {
    switch(realtimeEvent.event) {
      case 'enable_collab': // Fall through
      case 'disable_collab':
        if (user.uid !== realtimeEvent.data.uid || tabId !== realtimeEvent.data.tabId) {
          location.reload();
        }
        break;
      case 'saved_survey':
        if (realtimeEvent.data.username !== config?.user?.name || realtimeEvent.data.tabId !== tabId) {
          const collabResp = await getCollabState();
          const userActivity = Object.keys(collabResp.userActivity || {})
            .map(k => {
              return {
                lastEditedObject: collabResp.userActivity[k].lastEditedObject,
                uid: collabResp.userActivity[k].uid,
                name: collabResp.userActivity[k].name,
                tabId: collabResp.userActivity[k].tabId
              }
            });
          mergeReceivedEventsCallback(collabResp.events, userActivity, true, collabResp.debug, collabResp.unpublishedEvents);
          toast({
            description: `${realtimeEvent.data.username} just published!`,
            variant: 'success'
          })
        }
        break;
      case 'change':
        if (realtimeEvent.data.events?.length) {
          setCanPublish(false);
        }

        console.log("Merging", realtimeEvent.data.uuid, realtimeEvent.data.events.length, realtimeEvent.data.userActivity);
        if (debug) {
          console.log('got events', JSON.stringify(realtimeEvent.data.events));
        }
        let unpublishedEvents: ChangeSet = [];
        if (realtimeEvent.data.userActivity?.uid !== user.uid || realtimeEvent.data.userActivity?.tabId !== tabId) {
          unpublishedEvents = realtimeEvent.data.events;
        }
        mergeReceivedEventsCallback(realtimeEvent.data.events, realtimeEvent.data.userActivity ? [realtimeEvent.data.userActivity] : undefined, undefined, undefined, unpublishedEvents);
        break;
      case 'partial_change':
        if (realtimeEvent.data.compressedChunk?.length) {
          setCanPublish(false);
        }

        const currentlyCollected = eventsAwaitingMerge.current[realtimeEvent.data.uuid] || [];
        const decompressedEvents = decompress(realtimeEvent.data.compressedChunk) as ChangeSet;
        eventsAwaitingMerge.current[realtimeEvent.data.uuid] = [...currentlyCollected, decompressedEvents];

        // When all chunks have come in, we can go ahead and merge.
        if (eventsAwaitingMerge.current[realtimeEvent.data.uuid]?.length === realtimeEvent.data.totalChunks) {
          const flattenedEvents = eventsAwaitingMerge.current[realtimeEvent.data.uuid].reduce((acc, value) => acc.concat(value), []);
          console.log("Merging chunked changes", realtimeEvent.data.uuid, flattenedEvents.length, realtimeEvent.data.userActivity);
          mergeReceivedEventsCallback(flattenedEvents, realtimeEvent.data.userActivity ? [realtimeEvent.data.userActivity] : undefined);

          eventsAwaitingMerge.current[realtimeEvent.data.uuid] = [];
        }
        break;
      default: {
        // Ignore unexpected event
        return;
      }
    }

    if (incomingEditsTimer.current) clearTimeout(incomingEditsTimer.current);
    incomingEditsTimer.current = window.setTimeout(() => {
      setCanPublish(true);
    }, 3000);
  }, [config?.user?.name, toast, user.uid, mergeReceivedEventsCallback, getCollabState, tabId, debug]);
  
  const sendEvent = useChannel<ConfigRealtimeEvents>({
    realm: 'distro',
    name: channel,
    onMessage: eventCallback,
    onOpen: () => {
      onSocketConnected?.();
    },
    onClose: () => {
      onSocketDisconnected?.();
    },
    onError: () => {
      onSocketDisconnected?.();
    }
  });

  const alertJustPublished = useCallback(async () => {
    sendEvent({
      realm: 'distro',
      channel,
      event: 'saved_survey',
      data: {
        username: config?.user?.name,
        tabId
      }
    });
  }, [sendEvent, config?.user?.name, tabId, channel]);

  // Broadcasts events to other editors via WebSocket for real-time sync.
  // Note: WebSocket sends are fire-and-forget - we can detect if the socket is closed or if send throws,
  // but not if messages are silently lost in transit. DynamoDB is the source of truth;
  const doSendRealtimeEvents = useCallback(async (myActivity?: UserActivity, uuid1?: string) => {
    const toSend = removeDuplicateEvents(eventsToSend.current || []);
    if (toSend.length === 0) {
      return;
    }
    console.log('Sending events', uuid1, JSON.stringify(toSend));
    // We have a 32,768 byte (32 KB) limit on our production WebSocket API (AWS API Gateway).
    // If we have more than 100 events to send, let's split and compress them.
    let sendFailed = false;
    if (toSend.length > 100) {
      // Max compressed size is 30KB to leave room for the WebSocket message wrapper (~1-2KB)
      const MAX_COMPRESSED_SIZE = 30000;
      // Initial chunk size - 700 events typically compress to 17-26KB which fits under the limit.
      // Dynamic splitting handles any chunks that still exceed the limit after compression.
      const INITIAL_CHUNK_SIZE = 700;

      let hadOversizedEvent = false;

      // Split events that exceed the size limit after compression
      const splitIfNeededAndCompress = (events: ChangeSet): { events: ChangeSet, compressed: string }[] => {
        const compressed = compress(events);
        if (compressed.length <= MAX_COMPRESSED_SIZE) {
          return [{ events, compressed }];
        }
        // Cannot split a single event further - would cause infinite recursion
        if (events.length === 1) {
          hadOversizedEvent = true;
          return [{ events, compressed }];
        }
        // Split in half and recurse
        const mid = Math.floor(events.length / 2);
        const left = events.slice(0, mid);
        const right = events.slice(mid);
        return [...splitIfNeededAndCompress(left), ...splitIfNeededAndCompress(right)];
      };

      // First, create initial chunks of INITIAL_CHUNK_SIZE to avoid compressing huge data sets
      const initialChunks: ChangeSet[] = [];
      for (let i = 0; i < toSend.length; i += INITIAL_CHUNK_SIZE) {
        initialChunks.push(toSend.slice(i, i + INITIAL_CHUNK_SIZE));
      }

      // Then, compress each chunk and split further only if needed
      const chunks = initialChunks.flatMap(chunk => splitIfNeededAndCompress(chunk));

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const { compressed: compressedChunk } = chunks[chunkIndex];
        const result = sendEvent({
          realm: 'distro',
          channel,
          event: 'partial_change',
          data: {
            compressedChunk: compressedChunk,
            totalChunks: chunks.length,
            userActivity: myActivity,
            uuid: uuid1 || ''
          }
        });
        if (result?.status !== 'success') {
          // Keep only the unsent events for retry
          const unsentEvents = chunks.slice(chunkIndex).flatMap(c => c.events);
          eventsToSend.current = unsentEvents;
          sendFailed = true;
          break;
        }
        // Small delay between chunks to avoid overwhelming Lambda concurrency limits on large pastes
        await new Promise(resolve => setTimeout(resolve, 25));
      }
      if (!sendFailed) {
        eventsToSend.current = undefined;
      }
      if (hadOversizedEvent) {
        toast({
          description: 'Some changes were too large to sync with other users in real time. Have any collaborators wait a minute and then refresh their page to get your latest edits.',
          variant: 'error'
        });
      }
    } else {
      const result = sendEvent({
        realm: 'distro',
        channel,
        event: 'change',
        data: {
          events: toSend,
          userActivity: myActivity,
          uuid: uuid1 || ''
        }
      });
      // Only clear events if send succeeded - otherwise they'll be included in next send
      if (result?.status === 'success') {
        eventsToSend.current = undefined;
      } else {
        sendFailed = true;
      }
    }

    // If send failed and there are still events to send, schedule a retry
    if (sendFailed && eventsToSend.current && eventsToSend.current.length > 0) {
      sendRetryTimer.current = window.setTimeout(() => {
        doSendRealtimeEvents(myActivity, uuid1);
      }, 1000);
    }
  }, [sendEvent, channel, toast]);

  const writeNewCollabVersion = useCallback(async (params: {
    toSend: string,
    publish?: boolean,
    migrating?: boolean,
    commitMessage?: string,
    distroCommitId?: string,
    userActivity?: UserActivity,
    parentOpsVersion?: string,
  }) => {
    if (!branchId && !params.migrating) {
      return {
        status: 'no branchId',
      };
    }

    const { toSend, publish, migrating, commitMessage, distroCommitId, userActivity, parentOpsVersion } = params;
    const partSize = 5_000_000; // ~5MB
    // decide if the events toSend are too big to send over https. If so, 
    // we use multipart upload to send them in chunks straight to s3.
    const numParts = Math.ceil(toSend.length / partSize);
    let toReturn: {
      status: string,
      error?: string
    }
    if (numParts > 1) {
      const fileName = `${surveyName}/collabEvents.txt`; // Choose an appropriate file name
      const file = new File([toSend], fileName, { type: "text/plain" });

      const res = await multipartUpload.startUpload(file, {
        pathParams: {
          kind: 'collab',
          survey: surveyName
        },
        contentType: 'text/plain',
        length: toSend.length
      });
      
      const saveRes = await writeCollabVersion({
        name: surveyName,
        branchId,
        events: '',
        locationURL: res?.location,
        migrating,
        publish,
        commitMessage,
        distroCommitId,
        userActivity,
        parentOpsVersion,
      });
      
      toReturn = { 
        status: saveRes.status || (saveRes.error ? 'err' : 'ok'),
        error: saveRes.error || ''
      };
    } else {
      const saveRes = await writeCollabVersion({
        name: surveyName,
        branchId,
        events: toSend,
        locationURL: '',
        migrating,
        publish,
        commitMessage,
        distroCommitId,
        userActivity,
        parentOpsVersion,
      });

      toReturn = {
        status: saveRes.status || (saveRes.error ? 'err' : 'ok'),
        error: saveRes.error || ''
      };
    }
    return toReturn;
  }, [surveyName, writeCollabVersion, multipartUpload]);

  const enableCollab = useCallback(async (collabEvents: string, parentOpsVersion?: string | undefined) => {
    setCollabEnabled(true);

    const result = await writeNewCollabVersion({
      toSend: collabEvents,
      migrating: true,
      parentOpsVersion,
    });

    if (result?.status !== 'ok') {
      setCollabEnabled(false);
      toast({
        description: result.error || 'Failed to enable collab mode. Please try again.',
        variant: 'error'
      });
      return;
    }

    sendEvent({
      realm: 'distro',
      channel,
      event: 'enable_collab',
      data: {
        ...user, tabId
      }
    });
    return result;
  }, [sendEvent, writeNewCollabVersion, user, tabId, channel, toast]);

  const disableCollab = useCallback(async () => {
    setCollabEnabled(false);
    const result = await clearCollabEvents({ name: surveyName });

    if (result?.status !== 'ok') {
      setCollabEnabled(true);
      toast({
        description: result?.error || 'Failed to disable collab mode. Please try again.',
        variant: 'error'
      });
      return;
    }

    sendEvent({
      realm: 'distro',
      channel,
      event: 'disable_collab',
      data: {
        ...user, tabId
      }
    });
  }, [sendEvent, clearCollabEvents, user, tabId, channel, surveyName, toast]);

  // Attempts to save events to DynamoDB, returns true on success
  const attemptSaveEvents = useCallback(async (userActivity?: UserActivity) => {
    const currentEvents = eventsToSave.current;
    if (!currentEvents || currentEvents.length === 0) {
      return true; // Nothing to save
    }

    if (!branchId) {
      return false; // No branchId yet, try again when we have one
    }

    const uniqueEvents = removeDuplicateEvents(currentEvents);
    const toSend = JSON.stringify(uniqueEvents);
    if (debug) console.log('sending ', toSend);

    try {
      let saveSucceeded = false;
      // These collections of events can get big. If they're REALLY big, use writeNewVersion's multiupload
      // to upload them to s3 and save a new version automatically.
      if (toSend.length > 100000) {
        const result = await writeNewCollabVersion({
          toSend,
          userActivity
        });
        saveSucceeded = result.status === 'ok';
      } else {
        const result = await saveCollabEvents({
          name: surveyName,
          branchId,
          events: toSend,
          userActivity
        });
        saveSucceeded = result?.status === 'ok';
      }

      if (saveSucceeded) {
        eventsToSave.current = undefined;
        consecutiveSaveFailures.current = 0;
        return true;
      }
    } catch (error) {
      console.error('Error saving collab events:', error);
    }

    // Save failed - increment failure count and maybe alert
    consecutiveSaveFailures.current += 1;
    if (consecutiveSaveFailures.current >= 3) {
      toast({
        description: 'Having trouble saving your changes. Please check your connection.',
        variant: 'error'
      });
      // Reset counter so we don't spam alerts
      consecutiveSaveFailures.current = 0;
    }
    return false;
  }, [writeNewCollabVersion, saveCollabEvents, surveyName, debug, toast]);

  // This function handles sending new collab events over websockets to other listening clients,
  // and saves new collab events to dynamo (or s3, if they're really large)
  const handleNewCollabEvents = useCallback(async (events: ChangeSet, myActivity?: UserActivity, state?: State) => {
    const uuid1 = uuidv4();
    const thisRoundEvents = (eventsToSave.current ?? []).concat(events);

    // Cancel any pending save timers - this new debounce will include all pending events
    if (saveEventsTimer.current) clearTimeout(saveEventsTimer.current);
    if (saveRetryTimer.current) clearTimeout(saveRetryTimer.current);
    eventsToSave.current = thisRoundEvents;

    if (sendEventsTimer.current) clearTimeout(sendEventsTimer.current);
    if (sendRetryTimer.current) clearTimeout(sendRetryTimer.current);
    eventsToSend.current = (eventsToSend.current ?? []).concat(events);

    sendEventsTimer.current = window.setTimeout(() => {
      doSendRealtimeEvents(state ? state.myActivity : myActivity, uuid1);
    }, 100);

    const userActivity = state ? state.myActivity : myActivity;

    // We debounce 1 full second, then persist events to dynamo
    saveEventsTimer.current = window.setTimeout(async () => {
      const success = await attemptSaveEvents(userActivity);

      // If save failed and there are still events to save, schedule a retry,
      // This will get cleared if a new edit comes in before the retry happens.
      if (!success && eventsToSave.current && eventsToSave.current.length > 0) {
        saveRetryTimer.current = window.setTimeout(async () => {
          await attemptSaveEvents(userActivity);
        }, 3000);
      }
    }, 1000);
  }, [doSendRealtimeEvents, attemptSaveEvents]);

  useInterval(() => {
    if (collabEnabled && branchId && autosave) autoSaveCollabVersion({name: surveyName, branchId})
  }, 20 * 1000);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (collabEnabled && branchId && user.uid) {
        // Save any pending events that haven't been persisted yet
        const pendingEvents = eventsToSave.current ?? [];
        const uniqueEvents = removeDuplicateEvents(pendingEvents);

        saveCollabEvents({
          name: surveyName,
          branchId,
          events: JSON.stringify(uniqueEvents),
          userActivity: {
            uid: user.uid,
            name: config?.user?.name,
            tabId,
            lastEditedObject: ''
          }
        });

        // Send userActivity update over websockets too, so that existing distro viewers don't see you lingering.
        doSendRealtimeEvents({
          uid: user.uid,
          name: config?.user?.name,
          tabId,
          lastEditedObject: ''
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [doSendRealtimeEvents, saveCollabEvents, surveyName, tabId, config?.user?.name, user, collabEnabled]);

  // https://react.dev/reference/react/useCallback#optimizing-a-custom-hook
  // "If you’re writing a custom Hook, it’s recommended to wrap any functions that it returns into useCallback"
  return {
    collabEnabled,
    collabReady,
    branchId,
    canPublish,
    channel,
    alertJustPublished,
    getCollabState,
    handleNewCollabEvents,
    enableCollab,
    disableCollab,
    writeNewCollabVersion
  };
}
